import React, {Fragment, useEffect} from 'react';
import glamorous from 'glamorous';
import PropTypes from 'prop-types';
import AlternateDecisions from '../../../shared/library/cards/alternate-decision';
import {Query, compose} from 'react-apollo';
import {privateStackDecisions} from '../../../data/tool-profile/queries';
import Circular, {LARGE} from '../../../shared/library/indicators/indeterminate/circular';
import {ComponentContainer, Spinner, StyledSmallTitle, Center, MembersTitle} from './shared/styles';
import {flattenEdges} from '../../../shared/utils/graphql';
import {withMutation} from '../../../shared/enhancers/graphql-enhancer';
import {trackViews} from '../../../data/shared/mutations';
import {BASE_TEXT} from '../../../shared/style/typography';
import {TARMAC} from '../../../shared/style/colors';
import {withAnalyticsPayload} from '../../../shared/enhancers/analytics-enhancer';
import {updateQuery} from '../utils';
import LoadMoreButton from '../../../shared/library/buttons/load-more';
import PrivatePublicIndicator from '../../../shared/library/private-public-indicator';
import withErrorBoundary from '../../../shared/enhancers/error-boundary';
import {callTrackViews} from '../../../../app/shared/utils/trackViews.js';

const Container = glamorous.div({
  marginTop: 50
});

const Paragraph = glamorous.p({
  color: TARMAC,
  ...BASE_TEXT,
  marginTop: 8,
  lineHeight: 1.69
});

const sendTrackViewsMutation = ({loading, decisions, trackViews}) => {
  if (!loading && decisions) {
    const decisionIds = decisions.map(d => d.id);
    callTrackViews({trackViews, decisionIds, clientContext: `Reasons-${window.location.pathname}`});
  }
};

const PrivateDecisions = ({tool, trackViews, privateCompanyName}) => {
  return (
    <ComponentContainer>
      <Query
        query={privateStackDecisions}
        variables={{id: tool.id}}
        notifyOnNetworkStatusChange={true}
      >
        {({loading, data, fetchMore}) => {
          const hasData = data && data.tool;
          if (loading && !hasData)
            return (
              <Spinner>
                <Circular size={LARGE} />
              </Spinner>
            );

          if (hasData) {
            const endCursor = data.tool.privateStackDecisions.pageInfo.endCursor;
            const hasNextPage = data.tool.privateStackDecisions.pageInfo.hasNextPage;

            const onLoadMoreClick = () => {
              fetchMore({
                variables: {
                  after: endCursor,
                  first: 6
                },
                updateQuery
              });
            };
            const privateDecisions = flattenEdges(data.tool.privateStackDecisions);
            return (
              <PrivateDecisionsData
                loading={loading}
                privateDecisions={privateDecisions}
                trackViews={trackViews}
                privateCompanyName={privateCompanyName}
                tool={tool}
                hasNextPage={hasNextPage}
                onLoadMoreClick={onLoadMoreClick}
              />
            );
          } else {
            return null;
          }
        }}
      </Query>
    </ComponentContainer>
  );
};

PrivateDecisions.propTypes = {
  tool: PropTypes.object,
  trackViews: PropTypes.func,
  privateCompanyName: PropTypes.string
};

const PrivateDecisionsData = ({
  loading,
  privateDecisions,
  trackViews,
  privateCompanyName,
  tool,
  hasNextPage,
  onLoadMoreClick
}) => {
  useEffect(() => {
    sendTrackViewsMutation({loading, privateDecisions, trackViews});
  }, [privateDecisions]);

  return (
    <Container>
      {privateDecisions.length !== 0 && (
        <Fragment>
          <MembersTitle>
            <StyledSmallTitle>
              Private Decisions at {privateCompanyName} about {tool.name}
            </StyledSmallTitle>
            <PrivatePublicIndicator typeIndicator="BlueRoundLarge" />
          </MembersTitle>
          <Paragraph>
            Here are some stack decisions, common use cases and reviews by members of{' '}
            {privateCompanyName} with {tool.name} in their tech stack.
          </Paragraph>
        </Fragment>
      )}
      <AlternateDecisions decisions={privateDecisions} sendAnalyticsEvent={() => {}} />
      {hasNextPage && (
        <Center>
          <LoadMoreButton loading={loading} onClick={onLoadMoreClick} />
        </Center>
      )}
    </Container>
  );
};

PrivateDecisionsData.propTypes = {
  loading: PropTypes.bool,
  privateDecisions: PropTypes.array,
  trackViews: PropTypes.func,
  privateCompanyName: PropTypes.string,
  tool: PropTypes.object,
  hasNextPage: PropTypes.any,
  onLoadMoreClick: PropTypes.func
};

export default compose(
  withAnalyticsPayload(),
  withErrorBoundary(true),
  withMutation(trackViews, mutate => ({
    trackViews: (decisionIds, clientContext) => mutate({variables: {decisionIds, clientContext}})
  }))
)(PrivateDecisions);
